import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { apiGet, apiPost, apiPut, apiDelete } from '../../../shared/utils/apiConfig';
import { useTitle } from '../../../context/TitleContext';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaEye, FaBan, FaCheck, FaUser, FaPhone, FaMapMarkerAlt, FaSearch, FaFileExport, FaFilePdf } from 'react-icons/fa';
import { showSuccessToast, showErrorToast, showWarningToast, showInfoToast } from '../../../shared/utils/toastHelper';
import EnhancedPagination from '../../../shared/components/EnhancedPagination';
import axios from 'axios';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';

const BASE_URL = process.env.REACT_APP_BASE_URL;
const columnHelper = createColumnHelper();

const AdminClients = () => {
    const { setTitle } = useTitle();
    const navigate = useNavigate();
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [nameSearch, setNameSearch] = useState('');
    const [phoneSearch, setPhoneSearch] = useState('');
    const [debouncedNameSearch, setDebouncedNameSearch] = useState('');
    const [debouncedPhoneSearch, setDebouncedPhoneSearch] = useState('');
    /** فلتر الحالة: فارغ = الكل، 0 = غير محظور، 1 = محظور */
    const [statusFilter, setStatusFilter] = useState('');
    const [exporting, setExporting] = useState(false);
    const [exportingPdf, setExportingPdf] = useState(false);

    // Table states
    const [sorting, setSorting] = useState([]);

    // دالة لتصحيح عرض رقم الهاتف
    const formatPhoneNumber = (phone) => {
        if (!phone) return 'غير محدد';
        
        // تحويل إلى نص للتأكد
        let phoneStr = String(phone).trim();
        
        // إزالة أي مسافات أو رموز غير مرغوب فيها
        phoneStr = phoneStr.replace(/\s+/g, '');
        
        // إذا كان الرقم يبدأ بـ + - نتركه كما هو
        if (phoneStr.startsWith('+')) {
            return phoneStr;
        }
        
        // إذا كان الرقم ينتهي بـ + - نحوله إلى البداية
        if (phoneStr.endsWith('+')) {
            return '+' + phoneStr.slice(0, -1);
        }
        
        // إذا كان الرقم بدون + - نرجعه كما هو
        return phoneStr;
    };

    useEffect(() => {
        setTitle('إدارة المستخدمين');
    }, [setTitle]);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedNameSearch(nameSearch), 400);
        return () => clearTimeout(timer);
    }, [nameSearch]);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedPhoneSearch(phoneSearch), 400);
        return () => clearTimeout(timer);
    }, [phoneSearch]);

    useEffect(() => {
        if (currentPage !== 1) {
            setCurrentPage(1);
        }
    }, [debouncedNameSearch, debouncedPhoneSearch, statusFilter]);

    const getAuthToken = () => {
        const adminData = sessionStorage.getItem('adminkhadamat');
        if (adminData) {
            try {
                const parsed = JSON.parse(adminData);
                // البحث عن التوكن في أماكن مختلفة
                if (parsed.data?.token?.access_token) {
                    const token = String(parsed.data.token.access_token);
                    return token;
                }
                if (parsed.data?.access_token) {
                    const token = String(parsed.data.access_token);
                    return token;
                }
                if (parsed.data?.token) {
                    const token = String(parsed.data.token);
                    return token;
                }
                if (parsed.token) {
                    const token = String(parsed.token);
                    return token;
                }
                return null;
            } catch (error) {
                return null;
            }
        }
        return null;
    };

    const fetchClients = useCallback(async () => {
        try {
            setLoading(true);
            const token = getAuthToken();
         
            
            if (!token) {
                throw new Error('No authentication token found');
            }

            const params = {
                page: currentPage,
            };
            const nameQ = debouncedNameSearch.trim();
            const phoneQ = debouncedPhoneSearch.trim();
            if (nameQ) {
                params.name = nameQ;
            }
            if (phoneQ) {
                params.phone = phoneQ;
            }
            if (statusFilter === '0' || statusFilter === '1') {
                params.status = statusFilter;
            }
            
            const response = await axios.get(`${BASE_URL}/admin/clients`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                },
                params,
            });

            const apiStatus = response.data?.status;
            const responseStatus = response.status;
            
            if (apiStatus === 'success' || apiStatus === 200 || responseStatus === 200) {
                const clientsData = response.data.data?.data || response.data.data;
                // التأكد من أن البيانات هي array
                if (Array.isArray(clientsData)) {
                    setClients(clientsData);
                } else {
                    setClients([]);
                }
                // استخدام meta للـ pagination
                const meta = response.data.data?.meta || response.data.pagination;
                setTotalPages(meta?.last_page || meta?.total_pages || 1);
            } else {
                throw new Error(response.data.message || 'فشل في جلب المستخدمين');
            }
        } catch (error) {
            showErrorToast(error.response?.data?.message || 'فشل في جلب المستخدمين');
        } finally {
            setLoading(false);
        }
    }, [currentPage, debouncedNameSearch, debouncedPhoneSearch, statusFilter]);

    useEffect(() => {
        fetchClients();
    }, [fetchClients]);

    const handleBanUser = async (clientId, banStatus) => {
        try {
            const token = getAuthToken();
            const response = await axios.post(`${BASE_URL}/admin/clients/${clientId}/ban?ban=${banStatus}`, {}, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                },
            });

            const apiStatus = response.data?.status;
            const responseStatus = response.status;
            
            if (apiStatus === 'success' || apiStatus === 200 || responseStatus === 200) {
                showSuccessToast(banStatus === 1 ? 'تم حظر المستخدم بنجاح' : 'تم إلغاء حظر المستخدم بنجاح');
                fetchClients();
            } else {
                throw new Error(response.data.message || 'فشل في العملية');
            }
        } catch (error) {
            showErrorToast(error.response?.data?.message || 'فشل في العملية');
        }
    };

    const handleViewDetails = (clientId) => {
        navigate(`/admin-clients/${clientId}`);
    };

    // جلب كل العملاء (بنفس فلاتر البحث الحالية) عبر كل الصفحات
    const fetchAllFilteredClients = async () => {
        const token = getAuthToken();
        if (!token) {
            throw new Error('No authentication token found');
        }

        // فلاتر البحث الحالية (بدون رقم الصفحة)
        const baseParams = {};
        const nameQ = debouncedNameSearch.trim();
        const phoneQ = debouncedPhoneSearch.trim();
        if (nameQ) baseParams.name = nameQ;
        if (phoneQ) baseParams.phone = phoneQ;
        if (statusFilter === '0' || statusFilter === '1') baseParams.status = statusFilter;

        const headers = {
            'Authorization': `Bearer ${token}`,
            'Accept-Language': 'ar',
            'lang': 'ar',
        };

        const fetchPage = async (page) => {
            const res = await axios.get(`${BASE_URL}/admin/clients`, {
                headers,
                params: { ...baseParams, page },
            });
            const list = res.data?.data?.data || res.data?.data || [];
            const meta = res.data?.data?.meta || res.data?.pagination;
            const lastPage = meta?.last_page || meta?.total_pages || 1;
            return { list: Array.isArray(list) ? list : [], lastPage };
        };

        // الصفحة الأولى لمعرفة عدد الصفحات
        const first = await fetchPage(1);
        let allClients = [...first.list];
        for (let page = 2; page <= first.lastPage; page++) {
            const { list } = await fetchPage(page);
            allClients = allClients.concat(list);
        }
        return allClients;
    };

    // وصف الفلاتر الحالية (للعرض في رأس ملف PDF)
    const getActiveFiltersText = () => {
        const parts = [];
        const nameQ = debouncedNameSearch.trim();
        const phoneQ = debouncedPhoneSearch.trim();
        if (nameQ) parts.push(`الاسم: ${nameQ}`);
        if (phoneQ) parts.push(`الهاتف: ${phoneQ}`);
        if (statusFilter === '0') parts.push('الحالة: غير محظور');
        if (statusFilter === '1') parts.push('الحالة: محظور');
        return parts.length ? parts.join(' | ') : 'كل العملاء (بدون فلتر)';
    };

    // تصدير كل العملاء (بنفس فلاتر البحث الحالية) إلى ملف CSV يفتح في Excel
    const handleExport = async () => {
        if (exporting) return;
        try {
            setExporting(true);
            const allClients = await fetchAllFilteredClients();

            if (allClients.length === 0) {
                showWarningToast('لا توجد بيانات للتصدير');
                return;
            }

            // بناء ملف CSV
            const escapeCsv = (value) => {
                const str = value === null || value === undefined ? '' : String(value);
                if (/[",\n]/.test(str)) {
                    return `"${str.replace(/"/g, '""')}"`;
                }
                return str;
            };

            const columns = [
                { header: 'م', value: (_c, i) => i + 1 },
                { header: 'الكود', value: (c) => c.id ?? '' },
                { header: 'اسم العميل', value: (c) => c.name || 'غير محدد' },
                { header: 'رقم الهاتف', value: (c) => formatPhoneNumber(c.phone) },
                { header: 'البريد الإلكتروني', value: (c) => c.email || '' },
                { header: 'العنوان', value: (c) => c.default_address?.area || c.address || 'غير محدد' },
                { header: 'حالة الحظر', value: (c) => (c.ban === '1' || c.ban === 1 ? 'محظور' : 'غير محظور') },
            ];

            const headerRow = columns.map((col) => escapeCsv(col.header)).join(',');
            const dataRows = allClients.map((client, index) =>
                columns.map((col) => escapeCsv(col.value(client, index))).join(',')
            );
            const csvContent = '﻿' + [headerRow, ...dataRows].join('\r\n');

            // تنزيل الملف
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            const today = new Date().toISOString().slice(0, 10);
            link.href = url;
            link.setAttribute('download', `clients_${today}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            showSuccessToast(`تم تصدير ${allClients.length} عميل بنجاح`);
        } catch (error) {
            showErrorToast(error.response?.data?.message || 'فشل في تصدير البيانات');
        } finally {
            setExporting(false);
        }
    };

    // تصدير كل العملاء (بنفس الفلاتر) إلى PDF عبر نافذة طباعة المتصفح (يدعم العربي بشكل سليم)
    const handleExportPdf = async () => {
        if (exportingPdf) return;
        try {
            setExportingPdf(true);
            const allClients = await fetchAllFilteredClients();

            if (allClients.length === 0) {
                showWarningToast('لا توجد بيانات للتصدير');
                return;
            }

            const escapeHtml = (value) => {
                const str = value === null || value === undefined ? '' : String(value);
                return str
                    .replace(/&/g, '&amp;')
                    .replace(/</g, '&lt;')
                    .replace(/>/g, '&gt;')
                    .replace(/"/g, '&quot;');
            };

            const rowsHtml = allClients
                .map((c, i) => {
                    const isBanned = c.ban === '1' || c.ban === 1;
                    const address = c.default_address?.area || c.address || 'غير محدد';
                    return `
                        <tr>
                            <td>${i + 1}</td>
                            <td>${escapeHtml(c.id ?? '')}</td>
                            <td>${escapeHtml(c.name || 'غير محدد')}</td>
                            <td style="direction:ltr">${escapeHtml(formatPhoneNumber(c.phone))}</td>
                            <td>${escapeHtml(c.email || '-')}</td>
                            <td>${escapeHtml(address)}</td>
                            <td class="${isBanned ? 'banned' : 'active'}">${isBanned ? 'محظور' : 'غير محظور'}</td>
                        </tr>`;
                })
                .join('');

            const today = new Date().toLocaleDateString('ar-EG');
            const html = `<!DOCTYPE html>
<html lang="ar" dir="rtl">
<head>
<meta charset="utf-8" />
<title>قائمة العملاء</title>
<style>
    * { font-family: 'Cairo', 'Tahoma', 'Arial', sans-serif; }
    body { padding: 24px; color: #1f2937; }
    .header { text-align: center; margin-bottom: 8px; }
    .header h1 { margin: 0 0 6px; font-size: 22px; }
    .meta { text-align: center; color: #6b7280; font-size: 13px; margin-bottom: 18px; }
    table { width: 100%; border-collapse: collapse; font-size: 12px; }
    thead th {
        background: #667eea; color: #fff; padding: 8px 6px;
        border: 1px solid #5a67d8; text-align: center;
    }
    tbody td { padding: 7px 6px; border: 1px solid #e5e7eb; text-align: center; }
    tbody tr:nth-child(even) { background: #f8fafc; }
    .banned { color: #dc2626; font-weight: 700; }
    .active { color: #059669; font-weight: 700; }
    @media print { body { padding: 0; } thead { display: table-header-group; } }
</style>
</head>
<body>
    <div class="header">
        <h1>قائمة العملاء</h1>
    </div>
    <div class="meta">
        <div>الفلتر: ${escapeHtml(getActiveFiltersText())}</div>
        <div>إجمالي العملاء: ${allClients.length} &nbsp;|&nbsp; تاريخ التصدير: ${escapeHtml(today)}</div>
    </div>
    <table>
        <thead>
            <tr>
                <th>م</th>
                <th>الكود</th>
                <th>اسم العميل</th>
                <th>رقم الهاتف</th>
                <th>البريد الإلكتروني</th>
                <th>العنوان</th>
                <th>حالة الحظر</th>
            </tr>
        </thead>
        <tbody>${rowsHtml}</tbody>
    </table>
</body>
</html>`;

            const printWindow = window.open('', '_blank');
            if (!printWindow) {
                showErrorToast('من فضلك اسمح بالنوافذ المنبثقة (Pop-ups) لتصدير PDF');
                return;
            }
            printWindow.document.open();
            printWindow.document.write(html);
            printWindow.document.close();

            // ننتظر تحميل المحتوى ثم نفتح نافذة الطباعة (اختَر "حفظ كـ PDF")
            printWindow.onload = () => {
                printWindow.focus();
                printWindow.print();
            };

            showSuccessToast(`تم تجهيز ${allClients.length} عميل للطباعة / PDF`);
        } catch (error) {
            showErrorToast(error.response?.data?.message || 'فشل في تصدير البيانات');
        } finally {
            setExportingPdf(false);
        }
    };


    // تم إلغاء عرض حالة النشاط للمستخدمين

    const getStatusColor = (isBanned) => {
        return isBanned ? 'text-danger' : 'text-success';
    };

    // تعريف الأعمدة
    const columns = useMemo(
        () => [
            columnHelper.accessor('id', {
                id: 'id',
                header: '#',
                cell: ({ row }) => (
                    <div className="order-number-badge">
                        #{(currentPage - 1) * 10 + row.index + 1}
                    </div>
                ),
                size: 60,
                minSize: 60,
                maxSize: 60,
            }),
            columnHelper.display({
                id: 'image',
                header: 'الصورة',
                cell: ({ row }) => {
                    const client = row.original;
                    return (
                            <div className="client-avatar-container">
                                {client.prof_img ? (
                                    <img
                                        src={client.prof_img}
                                        alt={client.name || 'مستخدم'}
                                        className="client-avatar"
                                    />
                                ) : (
                                    <div className="default-placeholder">
                                        <FaUser />
                                    </div>
                                )}
                        </div>
                    );
                },
                size: 80,
                minSize: 70,
                maxSize: 90,
            }),
            columnHelper.accessor('name', {
                id: 'client',
                header: 'اسم العميل',
                cell: ({ row }) => (
                    <div className="client-name">
                        {row.original.name || 'غير محدد'}
                    </div>
                ),
                size: 150,
                minSize: 120,
                maxSize: 180,
            }),
            columnHelper.accessor('phone', {
                id: 'phone',
                header: 'رقم الهاتف',
                cell: ({ row }) => (
                    <div className="phone-badge">
                        <FaPhone className="mx-1" />
                        {formatPhoneNumber(row.original.phone)}
                    </div>
                ),
                size: 150,
                minSize: 130,
                maxSize: 180,
            }),
            columnHelper.accessor('default_address', {
                id: 'address',
                header: 'العنوان',
                cell: ({ row }) => (
                    <div className="address-wrap text-wrap">
                        <FaMapMarkerAlt className="mx-1" />
                        {row.original.default_address?.area || row.original.address || 'غير محدد'}
                    </div>
                ),
                size: 300,
                minSize: 250,
                maxSize: 400,
            }),
            columnHelper.accessor('ban', {
                id: 'banStatus',
                header: 'حالة الحظر',
                cell: ({ row }) => {
                    const isBanned = row.original.ban === "1" || row.original.ban === 1;
                    return (
                        <span className={`badge ${isBanned ? 'bg-danger' : 'bg-success'} text-white`}>
                            {isBanned ? 'محظور' : 'غير محظور'}
                        </span>
                    );
                },
                size: 120,
                minSize: 100,
                maxSize: 140,
            }),
            columnHelper.display({
                id: 'actions',
                header: 'الإجراءات',
                cell: ({ row }) => {
                    const client = row.original;
                    const isBanned = client.ban === "1" || client.ban === 1;
        return (
                        <div className="action-buttons">
                            <motion.button
                                whileHover={{ scale: 1.1, y: -2 }}
                                whileTap={{ scale: 0.95 }}
                                className="btn btn-view"
                                onClick={() => handleViewDetails(client.id)}
                                title="عرض التفاصيل">
                                <FaEye />
                            </motion.button>
                            {isBanned ? (
                                <motion.button
                                    whileHover={{ scale: 1.1, y: -2 }}
                                    whileTap={{ scale: 0.95 }}
                                    className="btn btn-unban"
                                    onClick={() => handleBanUser(client.id, 0)}
                                    title="إلغاء الحظر">
                                    <FaCheck />
                                </motion.button>
                            ) : (
                                <motion.button
                                    whileHover={{ scale: 1.1, y: -2 }}
                                    whileTap={{ scale: 0.95 }}
                                    className="btn btn-ban"
                                    onClick={() => handleBanUser(client.id, 1)}
                                    title="حظر المستخدم">
                                    <FaBan />
                                </motion.button>
                            )}
                        </div>
                    );
                },
                size: 120,
                minSize: 100,
                maxSize: 140,
            }),
        ],
        [currentPage]
    );

    // إعداد الجدول
    const table = useReactTable({
        data: clients || [],
        columns,
        state: {
            sorting,
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: {
            pagination: {
                pageSize: 10,
            },
        },
    });


    return (
        <div className="main_content">
            <div className="dashboard-container">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="mb-0">إدارة المستخدمين</h4>
                    <div className="d-flex gap-2">
                        <button
                            type="button"
                            className="btn btn-success d-flex align-items-center gap-2"
                            style={{ borderRadius: '8px' }}
                            onClick={handleExport}
                            disabled={exporting || exportingPdf || loading}
                            title="تصدير كل العملاء (بالفلاتر الحالية) إلى Excel"
                        >
                            {exporting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                    جاري التصدير...
                                </>
                            ) : (
                                <>
                                    <FaFileExport />
                                    تصدير Excel
                                </>
                            )}
                        </button>
                        <button
                            type="button"
                            className="btn btn-danger d-flex align-items-center gap-2"
                            style={{ borderRadius: '8px' }}
                            onClick={handleExportPdf}
                            disabled={exporting || exportingPdf || loading}
                            title="تصدير كل العملاء (بالفلاتر الحالية) إلى PDF"
                        >
                            {exportingPdf ? (
                                <>
                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                    جاري التصدير...
                                </>
                            ) : (
                                <>
                                    <FaFilePdf />
                                    تصدير PDF
                                </>
                            )}
                        </button>
                    </div>
                </div>

                <div className="card mb-4 shadow-sm border-0" style={{ borderRadius: '12px' }}>
                    <div className="card-body py-3">
                        <div className="row g-3 align-items-end">
                            <div className="col-md-6">
                                <label className="form-label small text-muted mb-1">البحث بالاسم</label>
                                <div className="input-group">
                                    <span className="input-group-text bg-white border-end-0">
                                        <FaSearch className="text-secondary" />
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control border-start-0"
                                        placeholder="ابحث بالاسم..."
                                        value={nameSearch}
                                        onChange={(e) => setNameSearch(e.target.value)}
                                        style={{ borderRadius: '0 8px 8px 0' }}
                                    />
                                </div>
                            </div>
                            <div className="col-md-4">
                                <label className="form-label small text-muted mb-1">البحث برقم الهاتف</label>
                                <div className="input-group">
                                    <span className="input-group-text bg-white border-end-0">
                                        <FaPhone className="text-secondary" />
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control border-start-0"
                                        placeholder="ابحث برقم الهاتف..."
                                        value={phoneSearch}
                                        onChange={(e) => setPhoneSearch(e.target.value)}
                                        style={{ borderRadius: '0 8px 8px 0' }}
                                    />
                                </div>
                            </div>
                            <div className="col-md-2">
                                <label className="form-label small text-muted mb-1">الحالة (status)</label>
                                <select
                                    className="form-select"
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    style={{ borderRadius: '8px' }}
                                >
                                    <option value="">جميع الحالات</option>
                                    <option value="0">غير محظور (0)</option>
                                    <option value="1">محظور (1)</option>
                                </select>
                            </div>
                            <div className="col-md-12">
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    style={{ borderRadius: '8px' }}
                                    onClick={() => {
                                        setNameSearch('');
                                        setPhoneSearch('');
                                        setDebouncedNameSearch('');
                                        setDebouncedPhoneSearch('');
                                        setStatusFilter('');
                                    }}
                                >
                                    مسح الفلاتر
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Clients Table */}
                <div className="card">
                    <div className="card-body">
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-3 text-muted">جاري تحميل المستخدمين...</p>
                            </div>
                        ) : !Array.isArray(clients) || clients.length === 0 ? (
                            <div className="text-center py-5">
                                <FaUser className="text-muted" style={{ fontSize: '3rem' }} />
                                <p className="mt-3 text-muted">لا توجد مستخدمين</p>
                            </div>
                        ) : (
                            <>
                                <div className="table-responsive">
                                    <table className="table table-modern">
                                        <thead>
                                            {table.getHeaderGroups().map(headerGroup => (
                                                <tr key={headerGroup.id}>
                                                    {headerGroup.headers.map(header => (
                                                        <th
                                                            key={header.id}
                                                            className="text-center"
                                                            style={{ width: header.getSize() }}
                                                        >
                                                            {header.isPlaceholder
                                                                ? null
                                                                : flexRender(
                                                                    header.column.columnDef.header,
                                                                    header.getContext()
                                                                )}
                                                        </th>
                                                    ))}
                                            </tr>
                                            ))}
                                        </thead>
                                        <tbody>
                                            {table.getRowModel().rows?.length ? (
                                                table.getRowModel().rows.map((row, index) => (
                                                    <motion.tr
                                                        key={row.id}
                                                        initial={{ opacity: 0, x: -20 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        transition={{ duration: 0.3, delay: index * 0.05 }}
                                                        className="table-row-modern"
                                                    >
                                                        {row.getVisibleCells().map(cell => (
                                                            <td key={cell.id} className="text-center">
                                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                    </td>
                                                        ))}
                                                    </motion.tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={columns.length} className="text-center py-4">
                                                        <div className="text-muted">
                                                            <FaUser className="mb-2" style={{ fontSize: '2rem' }} />
                                                            <p className="mb-0">لا توجد مستخدمين متاحين</p>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination */}
                                {totalPages > 1 && (
                                    <div className="card-footer bg-light py-2 px-2">
                                        <div className="row align-items-center g-0">
                                            <div className="col-md-6 px-2">
                                                <div className="d-flex align-items-center gap-1 flex-wrap">
                                                    <span className="text-muted">الانتقال إلى الصفحة</span>
                                                    <div className="input-group" style={{ width: 'auto' }}>
                                                        <span className="input-group-text" style={{ fontSize: '12px', color: '#6c757d', fontWeight: 'bold' }}>
                                                            #
                                                        </span>
                                                        <input
                                                            type="number"
                                                            className="form-control form-control-sm"
                                                            value={currentPage}
                                                            onChange={(e) => {
                                                                const value = parseInt(e.target.value);
                                                                if (!isNaN(value) && value > 0 && value <= totalPages) {
                                                                    setCurrentPage(value);
                                                                }
                                                            }}
                                                            min="1"
                                                            max={totalPages}
                                                            style={{ width: '60px' }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-6 px-2">
                                                <div className="d-flex justify-content-end gap-1 flex-wrap">
                                                    <button
                                                        className="btn btn-outline-secondary btn-sm"
                                                        onClick={() => setCurrentPage(1)}
                                                        disabled={currentPage <= 1}
                                                    >
                                                        الأولى
                                                    </button>
                                                    <button 
                                                        className="btn btn-outline-secondary btn-sm"
                                                        onClick={() => setCurrentPage(currentPage - 1)}
                                                        disabled={currentPage <= 1}
                                                    >
                                                        السابقة
                                                    </button>
                                                    <span className="d-flex align-items-center px-3">
                                                        صفحة {currentPage} من {totalPages}
                                                    </span>
                                                    <button 
                                                        className="btn btn-outline-secondary btn-sm"
                                                        onClick={() => setCurrentPage(currentPage + 1)}
                                                        disabled={currentPage >= totalPages}
                                                    >
                                                        التالية
                                                    </button>
                                                    <button 
                                                        className="btn btn-outline-secondary btn-sm"
                                                        onClick={() => setCurrentPage(totalPages)}
                                                        disabled={currentPage >= totalPages}
                                                    >
                                                        الأخيرة
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {false && table.getPageCount() > 1 && (
                                    <div className="card-footer bg-light">
                                        <div className="row align-items-center">
                                            <div className="col-md-6">
                                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                                    <span className="text-muted">الانتقال إلى الصفحة</span>
                                                    <div className="input-group" style={{ width: 'auto' }}>
                                                        <span className="input-group-text" style={{ fontSize: '12px', color: '#6c757d', fontWeight: 'bold' }}>
                                                            #
                                                        </span>
                                                        <input
                                                            type="number"
                                                            className="form-control form-control-sm"
                                                            value={table.getState().pagination.pageIndex + 1}
                                                            onChange={(e) => {
                                                                const value = parseInt(e.target.value);
                                                                if (!isNaN(value) && value > 0) {
                                                                    const page = value - 1;
                                                                    table.setPageIndex(page);
                                                                }
                                                            }}
                                                            min="1"
                                                            max={table.getPageCount()}
                                                            style={{ width: '60px' }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="d-flex justify-content-end gap-2 flex-wrap">
                                                    <button
                                                        className="btn btn-outline-secondary btn-sm"
                                                        onClick={() => table.setPageIndex(0)}
                                                        disabled={!table.getCanPreviousPage()}
                                                    >
                                                        الأولى
                                                    </button>
                                                    <button
                                                        className="btn btn-outline-secondary btn-sm"
                                                        onClick={() => table.previousPage()}
                                                        disabled={!table.getCanPreviousPage()}
                                                    >
                                                        السابقة
                                                    </button>
                                                    <span className="d-flex align-items-center px-3">
                                                        صفحة {table.getState().pagination.pageIndex + 1} من {table.getPageCount()}
                                                    </span>
                                                    <button
                                                        className="btn btn-outline-secondary btn-sm"
                                                        onClick={() => table.nextPage()}
                                                        disabled={!table.getCanNextPage()}
                                                    >
                                                        التالية
                                                    </button>
                                                    <button
                                                        className="btn btn-outline-secondary btn-sm"
                                                        onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                                                        disabled={!table.getCanNextPage()}
                                                    >
                                                        الأخيرة
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminClients;

// CSS للتصميم الحديث
const style = document.createElement('style');
style.textContent = `
  /* تصميم الجدول الحديث */
  .table-modern {
    background: white;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    border: none;
  }
  
  .table-modern thead {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
  }
  
  .table-modern thead th {
    border: none;
    padding: 1rem;
    font-weight: 600;
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  
  .table-modern tbody tr {
    border-bottom: 1px solid #f1f5f9;
    transition: all 0.3s ease;
  }
  
  .table-modern tbody tr:hover {
    background-color: #f8fafc;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  .table-modern tbody td {
    border: none;
    padding: 1rem;
    vertical-align: middle;
  }
  
  /* تصميم رقم العميل */
  .order-number-badge {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    padding: 0.5rem 1rem;
    border-radius: 20px;
    font-weight: 600;
    font-size: 0.875rem;
    display: inline-block;
    min-width: 60px;
    text-align: center;
  }
  
  /* تصميم معلومات العميل */
  .client-info {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.75rem;
    justify-content: center;
  }
  
  /* تصميم صورة العميل */
  .client-avatar-container {
    display: flex;
    justify-content: center;
    align-items: center;
  }
  
  .client-avatar {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    object-fit: cover;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  /* أيقونات افتراضية للبيانات الفارغة */
  .default-placeholder {
    width: 50px;
    height: 50px;
    background: #f8fafc;
    border: 2px dashed #cbd5e1;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #94a3b8;
    font-size: 1.2rem;
    transition: all 0.3s ease;
  }
  
  .default-placeholder:hover {
    background: #f1f5f9;
    border-color: #94a3b8;
    color: #64748b;
  }
  
  /* تصميم اسم العميل */
  .client-name {
    font-weight: 500;
    color: #374151;
    max-width: 150px;
    text-align: center;
    display: block;
    margin: 0 auto;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  /* تصميم رقم الهاتف */
  .phone-badge {
    background: #f0f9ff;
    color: #0369a1;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    font-weight: 500;
    font-size: 0.875rem;
    border: 1px solid #bae6fd;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.25rem;
  }
  

  /* تصميم العنوان */
  .address-display {
    background: #f8fafc;
    color: #64748b;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    font-size: 0.875rem;
    border: 1px solid #e2e8f0;
    min-width: 300px;
    max-width: 500px;
    white-space: normal;
    word-wrap: break-word;
    word-break: break-word;
    overflow: visible;
    display: inline-block;
    text-align: center;
    line-height: 1.4;
    vertical-align: middle;
  }
  
  /* تصميم العنوان الجديد مع text-wrap */
  .address-wrap {
    background: #f8fafc;
    color: #64748b;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    font-size: 0.875rem;
    border: 1px solid #e2e8f0;
    min-width: 300px;
    max-width: 500px;
    overflow: visible;
    display: inline-block;
    text-align: center;
    line-height: 1.4;
    vertical-align: middle;
  }
  
  /* تصميم الأيقونة داخل العنوان */
  .address-display svg,
  .address-wrap svg {
    display: inline-block;
    vertical-align: middle;
    margin-right: 0.25rem;
    flex-shrink: 0;
  }

  /* إصلاح overflow في الجدول */
  .table-modern td:has(.address-display),
  .table-modern td:has(.address-wrap) {
    min-width: 350px !important;
    max-width: none !important;
    overflow: visible !important;
    white-space: normal !important;
  }
  
  /* تصميم أزرار الإجراءات */
  .action-buttons {
    display: flex;
    gap: 0.5rem;
    justify-content: center;
    flex-direction: row;
    flex-wrap: nowrap;
  }
  
  .btn-view, .btn-ban, .btn-unban {
    border: none;
    color: white !important;
    padding: 0.25rem;
    border-radius: 8px;
    font-size: 1.2rem;
    font-weight: 500;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    width: 36px;
    height: 36px;
  }
  
  .btn-ban {
    font-size: 1rem;
  }
  
  .btn-view {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    box-shadow: 0 2px 4px rgba(102, 126, 234, 0.3);
  }
  
  .btn-ban {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    box-shadow: 0 2px 4px rgba(239, 68, 68, 0.3);
  }
  
  .btn-unban {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    box-shadow: 0 2px 4px rgba(16, 185, 129, 0.3);
  }
  
  .btn-view:hover, .btn-ban:hover, .btn-unban:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
  
  .btn-view * {
    color: white !important;
  }
  
  /* تحسين البادج */
  .badge {
    padding: 0.5rem 1rem;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  
  /* إصلاح الباجينيشن */
  .pagination .page-item.active .page-link {
    background-color: #667eea !important;
    border-color: #667eea !important;
    color: white !important;
  }
  .pagination .page-item .page-link {
    color: #667eea;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    margin: 0 2px;
    padding: 0.5rem 0.75rem;
  }
  .pagination .page-item .page-link:hover {
    color: #5a67d8;
    background-color: #f8fafc;
    border-color: #cbd5e1;
  }
`;
document.head.appendChild(style);
